import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Skull } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { EventCard, formatYear } from "@/components/EventCard";
import { setSeo } from "@/lib/events";

const CAUSE_STYLE = { Executed: "bg-ink text-gold", Assassinated: "bg-[#7a1a1d] text-white", Murdered: "bg-[#7a1a1d] text-white", Shot: "bg-ink text-white", "Killed in battle": "bg-ink text-white", Suicide: "bg-paper text-ink border border-ink", Accident: "bg-paper text-ink border border-[#e5e7eb]", "Died in prison": "bg-paper text-ink border border-ink" };

export default function Died() {
  const [params, setParams] = useSearchParams();
  const cause = params.get("cause") || "";
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/infamous", { params: { cause: cause || undefined } }).then((r) => setData(r.data)).catch(() => setData({ total: 0, causes: [], eras: [] }));
  }, [cause]);

  useEffect(() => {
    setSeo({
      title: `Died on this day — ${cause ? `${cause.toLowerCase()}, ` : ""}executed, assassinated and murdered through history | OddlyTrue`,
      description: "Every figure in the OddlyTrue archive who met a violent or untimely end — the guillotine, the gallows, the assassin's bullet, the battlefield — era by era, with the date they died.",
      canonical: `https://oddlytrue.co/died${cause ? `?cause=${encodeURIComponent(cause)}` : ""}`,
    });
  }, [cause]);

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="died-page">
        <div className="flex items-center gap-2">
          <Skull className="w-4 h-4 text-gold" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Died on this day</p>
        </div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold text-ink leading-tight">Executed, assassinated, murdered</h1>
        <p className="mt-3 text-base text-ink-muted max-w-2xl">Every figure in the OddlyTrue archive who met a violent or untimely end — the guillotine, the gallows, the assassin's bullet, the battlefield — in date order, era by era. Tap a date to see everything else that happened that day.</p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="died-causes">
          <button type="button" data-testid="died-cause-all" onClick={() => setParams({}, { replace: true })} className={`px-4 h-9 rounded-full text-[13px] font-semibold border transition-colors ${!cause ? "bg-ink border-ink text-white" : "bg-white border-[#e5e7eb] text-ink-muted hover:border-gold"}`}>
            All {data ? `(${data.causes.reduce((n, c) => n + c.count, 0)})` : ""}
          </button>
          {(data?.causes || []).map((c) => (
            <button key={c.cause} type="button" data-testid={`died-cause-${c.cause.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setParams({ cause: c.cause }, { replace: true })}
              className={`px-4 h-9 rounded-full text-[13px] font-semibold border transition-colors ${cause === c.cause ? "bg-ink border-ink text-white" : "bg-white border-[#e5e7eb] text-ink-muted hover:border-gold"}`}>
              {c.cause} <span className="opacity-60">{c.count}</span>
            </button>
          ))}
        </div>

        <AdSlot id="adsense-died" slot="TOP" size="leaderboard" className="my-8" />

        {!data ? (
          <p className="text-sm text-ink-muted">Opening the ledger…</p>
        ) : data.eras.length === 0 ? (
          <p data-testid="died-empty" className="text-sm text-ink-muted">Nothing recorded yet for this cause.</p>
        ) : (
          <div className="space-y-12">
            {data.eras.map((era) => (
              <section key={era.era} data-testid={`died-era-${era.era.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-baseline justify-between border-b-2 border-ink pb-2 mb-5">
                  <h2 className="text-2xl font-bold text-ink">{era.era}</h2>
                  <span className="text-[12px] font-semibold text-ink-muted">{era.from < 0 ? "" : `${formatYear(era.from)}–${era.to}`} · {era.count}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {era.items.map((e, i) => (
                    <div key={e.id} className="relative">
                      <span className={`absolute -top-2 left-4 z-10 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm ${CAUSE_STYLE[e.death_cause] || "bg-ink text-gold"}`}>{e.death_cause}</span>
                      <EventCard e={e} i={i} total={era.items.length} showDate />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
